import { Injectable } from '@angular/core';
import { ENUM_PERMISSIONS, PERMISSION_LABELS } from '../enums/permission.enum';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';

/**
 * Construit les messages « permission manquante » affichés à l'utilisateur.
 * Source unique du wording, partagée entre le PermissionGuard (routes),
 * la directive permissionBlock (boutons verrouillés) et les gardes de handlers.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionMessageService {
  /** Libellé acté par le client (recette PRED-3233 du 15/09/2026) pour tout refus d'accès à un écran. */
  static readonly ACCESS_DENIED_MESSAGE = 'Accès non autorisé';

  constructor(
    private authService: AuthService,
    private snackbarService: SnackbarService,
  ) {}

  buildMissingPermissionMessage(permission: ENUM_PERMISSIONS | ENUM_PERMISSIONS[]): string {
    const permissions = Array.isArray(permission) ? permission : [permission];
    const details = permissions
      .map(code => `${PERMISSION_LABELS[code] ?? 'demander à un administrateur'}`)
      .join(' ou ');

    return `Permission requise : ${details}`;
  }

  /**
   * Message de refus d'accès à un écran (toast du PermissionGuard, page forbidden…).
   * Sans code : le libellé seul. Avec code(s) : suivi de la permission à demander.
   */
  buildAccessDeniedMessage(permission?: ENUM_PERMISSIONS | ENUM_PERMISSIONS[]): string {
    return permission
      ? `${PermissionMessageService.ACCESS_DENIED_MESSAGE}. ${this.buildMissingPermissionMessage(permission)}`
      : PermissionMessageService.ACCESS_DENIED_MESSAGE;
  }

  /**
   * Garde pour les actions hors de portée de la directive permissionBlock (ex. boutons rendus
   * par la datatable partagée) : le bouton reste visible, le clic affiche la snackbar.
   * Accepte un code ou un tableau (OU logique). Retourne true si l'action doit être bloquée.
   */
  blockIfMissingPermission(permission: ENUM_PERMISSIONS | ENUM_PERMISSIONS[]): boolean {
    const permissions = Array.isArray(permission) ? permission : [permission];
    if (permissions.some(p => this.authService.hasPermission(p))) {
      return false;
    }
    this.snackbarService.danger(this.buildMissingPermissionMessage(permission));
    return true;
  }
}
